import {
	FeedbackResponseForm,
	FeedbackResponseSaveRequest,
	FeedbackShowResource,
	Id,
	Loading,
	Stepper,
	StepperStep,
	createFeedbackResponseService,
	isSuccess,
} from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router";
import { eventFeedbackService } from "../../shared/api/eventFeedbackService";
import { eventService } from "../../shared/api/eventService";

interface Props {
	eventId: Id;
}

interface EventFeedbackResponseFormProps {
	feedback: FeedbackShowResource;
	eventId: Id;
	onSubmitted: () => Promise<void>;
}

const EventFeedbackResponseForm: FC<EventFeedbackResponseFormProps> = ({ feedback, eventId, onSubmitted }) => {
	const { t } = useTranslation();
	const feedbackResponseService = useMemo(
		() => createFeedbackResponseService(["events", `${eventId}`], feedback.id),
		[eventId, feedback.id],
	);

	const handleSubmit = async (request: FeedbackResponseSaveRequest) => {
		const response = await feedbackResponseService.createResponse(request);
		if (!isSuccess(response)) {
			return false;
		}
		await onSubmitted();
		return true;
	};

	return <FeedbackResponseForm key={feedback.id} feedback={feedback} onSubmit={handleSubmit} submitTitle={t("Next")} />;
};

export const EventFeedback: FC<Props> = ({ eventId }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { data: feedback, refetch } = useQuery({
		queryKey: eventFeedbackService.eventFeedbackKey(eventId),
		queryFn: () => eventFeedbackService.eventFeedback(eventId).then((response) => response.data.data),
	});

	const activeFeedbackIdx = useMemo(() => {
		if (!feedback) return -1;
		return feedback.findIndex((feedbackItem) => !feedbackItem.hasUserResponded);
	}, [feedback]);

	const steps: StepperStep[] = useMemo(() => {
		return (feedback || []).map(
			(feedback, index): StepperStep => ({
				label: feedback.name,
				status: activeFeedbackIdx === index ? "active" : feedback.hasUserResponded ? "done" : "inactive",
				optional: false,
			}),
		);
	}, [activeFeedbackIdx, feedback]);

	if (!feedback) {
		return <Loading />;
	}

	if (activeFeedbackIdx < 0) {
		return <Navigate to=".." replace />;
	}

	const handleFeedbackSubmitted = async () => {
		if (activeFeedbackIdx + 1 < feedback.length) {
			await refetch();
		} else {
			navigate("..", { replace: true });
			void queryClient.invalidateQueries({ queryKey: eventFeedbackService.eventFeedbackKey(eventId) });
			void queryClient.invalidateQueries({ queryKey: eventService.detailsKey(eventId) });
		}
	};

	return (
		<div>
			{feedback.length > 1 && <Stepper steps={steps} currentStepIndex={activeFeedbackIdx} />}
			<EventFeedbackResponseForm
				eventId={eventId}
				feedback={feedback[activeFeedbackIdx]}
				onSubmitted={handleFeedbackSubmitted}
			/>
		</div>
	);
};
